import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { databaseApi } from "@/lib/api";
import { DatabaseDetails, QuotaStats } from "@/types/database";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle,
  Clock,
  Crown,
  Database,
  ExternalLink,
  HardDrive,
  TrendingUp,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";

interface QuotaUsageTabProps {
  isActive: boolean;
  databases: DatabaseDetails[];
}

interface DatabaseUsage {
  [dbname: string]: {
    used: number;
    total: number;
    percentage: number;
    status: "low" | "medium" | "high";
  };
}

const QuotaUsageTab: React.FC<QuotaUsageTabProps> = ({
  isActive,
  databases,
}) => {
  const [quotaStats, setQuotaStats] = useState<QuotaStats | null>(null);
  const [databaseUsage, setDatabaseUsage] = useState<DatabaseUsage>({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isActive) {
      loadQuotaStats();
      loadDatabaseUsage();
    }
  }, [isActive, databases]);

  const loadQuotaStats = async () => {
    setLoading(true);
    try {
      const response = await databaseApi.getQuotaStats();
      if (response.success && response.data) {
        setQuotaStats(response.data.stats);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load quota information",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDatabaseUsage = async () => {
    try {
      const usageData: DatabaseUsage = {};

      // Fetch usage data for each active database
      for (const database of databases) {
        if (database.status === "active") {
          try {
            const response = await databaseApi.getDatabaseUsage(
              database.dbname
            );
            if (response.success && response.data) {
              usageData[database.dbname] = response.data.usage;
            }
          } catch (error) {
            toast({
              title: "Error loading usage data",
              description: `Failed to load usage metrics for ${database.dbname}:`,
              status: "error",
            });
            // Set default values if usage fetch fails
            usageData[database.dbname] = {
              used: 0,
              total: parseSizeToBytes(database.size),
              percentage: 0,
              status: "low",
            };
          }
        }
      }

      setDatabaseUsage(usageData);
    } catch (error) {
      toast({
        title: "Error loading usage data",
        description: `Failed to load usage metrics`,
        status: "error",
      });
    }
  };

  const getDatabaseIcon = (dbtype: string) => {
    switch (dbtype) {
      case "MYSQL":
        return "🐬";
      case "POSTGRES":
        return "🐘";
      case "MONGODB":
        return "🍃";
      default:
        return "🗄️";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "pending_delete":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "deleted":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "pending_delete":
        return "bg-yellow-100 text-yellow-800";
      case "deleted":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStorageUsageColor = (percentage: number) => {
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 75) return "text-yellow-600";
    if (percentage >= 50) return "text-blue-600";
    return "text-green-600";
  };

  const getStorageUsageBarColor = (percentage: number) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-yellow-500";
    if (percentage >= 50) return "bg-blue-500";
    return "bg-green-500";
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const parseSizeToBytes = (size: string): number => {
    const match = size.match(/^(\d+)(MB|GB|TB)$/i);
    if (!match) return 0;
    const num = parseInt(match[1], 10);
    const unit = match[2].toUpperCase();
    switch (unit) {
      case "MB":
        return num * 1024 * 1024;
      case "GB":
        return num * 1024 * 1024 * 1024;
      case "TB":
        return num * 1024 * 1024 * 1024 * 1024;
      default:
        return 0;
    }
  };

  const calculateTotalStorageUsed = (): number => {
    return Object.values(databaseUsage).reduce((total, usage) => {
      return total + usage.used;
    }, 0);
  };

  const getDatabaseTypeCounts = () => {
    const counts = { mysql: 0, postgres: 0, mongodb: 0, pending: 0 };
    databases.forEach((db) => {
      if (db.status === "suspended") {
        counts.pending++;
      } else if (db.status === "active") {
        counts[db.dbtype as keyof typeof counts]++;
      }
    });
    return counts;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading quota information...</span>
      </div>
    );
  }

  const typeCounts = getDatabaseTypeCounts();
  const totalStorageUsed = calculateTotalStorageUsed();
  const totalStorageUsedFormatted = formatBytes(totalStorageUsed);

  return (
    <div className="space-y-6">
      {/* Tier Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {quotaStats?.tier === "PAID" ? (
                <Crown className="h-6 w-6 text-yellow-500" />
              ) : (
                <Database className="h-6 w-6 text-blue-500" />
              )}
              <div>
                <CardTitle className="text-xl">
                  {quotaStats?.tier === "PAID" ? "Pro Plan" : "Free Plan"}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {quotaStats?.tier === "PAID"
                    ? "Unlimited databases and storage"
                    : "Limited databases and storage"}
                </p>
              </div>
            </div>
            {quotaStats?.tier === "FREE" && (
              <Button
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                onClick={() => {
                  toast({
                    title: "Upgrade to Pro",
                    description: "Pro plan features coming soon!",
                    status: "success",
                  });
                }}
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Storage Usage Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            <CardTitle>Storage Usage</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                Total Storage Used (Actual)
              </span>
              <span
                className={`font-bold ${getStorageUsageColor(
                  quotaStats?.storage.percentage || 0
                )}`}
              >
                {totalStorageUsedFormatted}
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Storage Quota</span>
                <span>
                  {quotaStats?.storage.formatted.total || "Unlimited"}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getStorageUsageBarColor(
                    quotaStats?.storage.percentage || 0
                  )}`}
                  style={{
                    width: `${Math.min(
                      quotaStats?.storage.percentage || 0,
                      100
                    )}%`,
                  }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{totalStorageUsedFormatted}</span>
                <span>
                  {quotaStats?.storage.formatted.total || "Unlimited"}
                </span>
              </div>
            </div>

            {quotaStats?.tier === "FREE" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Free Tier Limits</p>
                    <p className="text-xs mt-1">
                      • Maximum {quotaStats?.databases.limits.perType} databases
                      per type
                      <br />• Maximum {quotaStats?.databases.limits.total} total
                      databases ({quotaStats?.databases.limits.perType} × 3
                      types)
                      <br />• {quotaStats?.storage.formatted.total} total
                      storage quota
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Database Statistics Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            <CardTitle>Database Statistics</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {typeCounts.mysql}
              </div>
              <div className="text-sm text-green-800">MySQL</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {typeCounts.postgres}
              </div>
              <div className="text-sm text-blue-800">PostgreSQL</div>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600">
                {typeCounts.mongodb}
              </div>
              <div className="text-sm text-emerald-800">MongoDB</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {typeCounts.pending}
              </div>
              <div className="text-sm text-yellow-800">Pending</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Database Details Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            <CardTitle>Database Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 font-medium">Database</th>
                  <th className="text-left py-3 px-2 font-medium">Type</th>
                  <th className="text-left py-3 px-2 font-medium">
                    Allocated Size
                  </th>
                  <th className="text-left py-3 px-2 font-medium">
                    Used Storage
                  </th>
                  <th className="text-left py-3 px-2 font-medium">Status</th>
                  <th className="text-left py-3 px-2 font-medium">Class</th>
                  <th className="text-left py-3 px-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {databases.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No databases found
                    </td>
                  </tr>
                ) : (
                  databases.map((database) => {
                    const usage = databaseUsage[database.dbname];
                    const usedStorage = usage ? formatBytes(usage.used) : "N/A";
                    const usagePercentage = usage ? usage.percentage : 0;

                    return (
                      <tr
                        key={database.id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {getDatabaseIcon(database.dbtype)}
                            </span>
                            <span className="font-medium">
                              {database.dbname}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <span className="capitalize">{database.dbtype}</span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="font-mono">{database.size}</span>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`font-mono ${getStorageUsageColor(
                                usagePercentage
                              )}`}
                            >
                              {usedStorage}
                            </span>
                            {usage && (
                              <div className="w-full bg-gray-200 rounded-full h-1">
                                <div
                                  className={`h-1 rounded-full ${getStorageUsageBarColor(
                                    usagePercentage
                                  )}`}
                                  style={{
                                    width: `${Math.min(usagePercentage, 100)}%`,
                                  }}
                                ></div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              database.status
                            )}`}
                          >
                            {getStatusIcon(database.status)}
                            {database.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              database.databaseClass === "PROD"
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {database.databaseClass === "PROD" ? (
                              <Zap className="h-3 w-3" />
                            ) : (
                              <TrendingUp className="h-3 w-3" />
                            )}
                            {database.databaseClass}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-muted-foreground">
                          {new Date(database.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pro Plan Benefits */}
      {quotaStats?.tier === "FREE" && (
        <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-purple-800">
                Upgrade to Pro Plan
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">∞</div>
                <div className="text-sm text-purple-800">
                  Unlimited Databases
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">∞</div>
                <div className="text-sm text-purple-800">Unlimited Storage</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">∞</div>
                <div className="text-sm text-purple-800">No Size Limits</div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Button
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                onClick={() => {
                  toast({
                    title: "Pro Plan Coming Soon",
                    description:
                      "Advanced features and unlimited resources will be available soon!",
                    status: "success",
                  });
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Learn More About Pro Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuotaUsageTab;
